/* People · drivers and parents. Design: ConsolePeople.dc.html.
   Deep link: ?tab=drivers|parents&q=<text>&person=<uid>. Inviting, adding a
   driver and managing an account happen here since 2026-10-28 (they opened
   the classic dashboard's Accounts tab before). */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFleet } from '../data/FleetData';
import type { Bus, Person } from '../data/model';
import { usePage } from '../Shell';
import { AddDriverModal, FamilyLinkRequests, InviteModal, PersonSheet, UnlinkedModal } from '../PeopleModals';
import { ChipTone, Empty, NoResults, PageBody, PageSkeleton, StaleBanner, ago, initials, n0, plural, useGo } from '../kit';

type Tab = 'drivers' | 'parents';

/** "0712 •• 418" — enough to recognise a number, not enough to copy it. */
function maskPhone(raw: string): string {
  let d = raw.replace(/\D/g, '');
  if (!d) return '';
  if (d.startsWith('254') && d.length >= 12) d = '0' + d.slice(3);
  if (d.length < 7) return '••';
  return `${d.slice(0, 4)} •• ${d.slice(-3)}`;
}

const Dash = () => <span style={{ color: 'var(--faint)' }}>—</span>;

/* Children's names only (Gabriel, 2026-10-28) — the class is on the person
   panel. Two fit; the rest are "+N more", which opens the full list in place
   without opening the panel. A long name ends in an ellipsis. */
const ChildNames: React.FC<{ names: string[] }> = ({ names }) => {
  const [all, setAll] = useState(false);
  if (!names.length) return <Dash />;
  const shown = all ? names : names.slice(0, 2);
  const rest = names.length - shown.length;
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: all ? 'wrap' : 'nowrap', minWidth: 0, whiteSpace: all ? 'normal' : 'nowrap' }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }} title={names.join(', ')}>{shown.join(', ')}</span>
      {rest > 0 && (
        <button type="button" onClick={(e) => { e.stopPropagation(); setAll(true); }}
          style={{ all: 'unset', cursor: 'pointer', color: 'var(--teal-ink)', fontWeight: 600, flex: 'none' }}
          aria-label={`Show all ${names.length} children`}>…+{rest} more</button>
      )}
      {all && names.length > 2 && (
        <button type="button" onClick={(e) => { e.stopPropagation(); setAll(false); }}
          style={{ all: 'unset', cursor: 'pointer', color: 'var(--muted)', fontWeight: 600, flex: 'none' }}>Show less</button>
      )}
    </span>
  );
};

const People: React.FC = () => {
  const f = useFleet();
  const go = useGo();
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get('tab') === 'parents' ? 'parents' : 'drivers';
  const q = params.get('q') || '';

  const setParam = (k: 'tab' | 'q', v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };

  const [inviting, setInviting] = useState(false);
  /* ?invite=drivers|parents opens the invite dialog — Zana's "invite a driver"
     lands here (data/zanaExec). The parameter is dropped once it is open. */
  const inviteParam = params.get('invite');
  useEffect(() => {
    if (inviteParam !== 'drivers' && inviteParam !== 'parents') return;
    const next = new URLSearchParams(params);
    next.delete('invite');
    next.set('tab', inviteParam);
    setParams(next, { replace: true });
    setInviting(true);
  }, [inviteParam, params, setParams]);
  const [addingDriver, setAddingDriver] = useState(false);
  const [unlinkedOpen, setUnlinkedOpen] = useState(false);
  const personId = params.get('person');
  const person = personId ? [...f.drivers, ...f.parents].find((x) => x.id === personId) || null : null;
  const openPerson = (id: string) => { const next = new URLSearchParams(params); next.set('person', id); setParams(next, { replace: !!personId }); };
  const closePerson = useCallback(() => { const next = new URLSearchParams(params); next.delete('person'); setParams(next, { replace: true }); }, [params, setParams]);
  const [sheetTop, setSheetTop] = useState(64);
  useEffect(() => {
    if (!person) return undefined;
    const h = (document.querySelector('.tc .main > .top') as HTMLElement | null)?.offsetHeight;
    if (h) setSheetTop(h);
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.querySelector('[role=dialog]')) { e.preventDefault(); closePerson(); } };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [person, closePerson]);

  usePage({
    key: 'ConsolePeople', title: 'Drivers and parents', zanaContext: `People · ${tab}`,
    zanaIntro: 'I can find people, check who gets alerts and draft messages.',
    prompts: [
      ...(f.childrenUnlinked > 0 ? [`Which ${plural(f.childrenUnlinked, 'child has', 'children have')} no parent?`] : []),
      'Which drivers have not been seen this week?',
      'Draft an invite reminder',
    ],
  });

  const busOf = useMemo(() => {
    const byId = new Map(f.buses.map((b) => [b.id, b]));
    return (p: Person): Bus | undefined => (p.busId ? byId.get(p.busId) : undefined) || f.buses.find((b) => b.driverId === p.id);
  }, [f.buses]);
  const busName = useMemo(() => {
    const byId = new Map(f.buses.map((b) => [b.id, b.name]));
    return (id: string | null) => (id ? byId.get(id) || '' : '');
  }, [f.buses]);

  /** Today's state of the driver's bus, in the design's words. */
  const today = (p: Person): { tone: ChipTone; label: string } => {
    const b = busOf(p);
    if (!b) return { tone: 'mute', label: 'No bus' };
    if (b.status === 'on_route') return { tone: 'ok', label: 'On a run' };
    if (b.status === 'late') return { tone: 'warn', label: 'Running late' };
    if (b.status === 'off_road') return { tone: 'bad', label: 'Bus off road' };
    const todayStr = new Date().toDateString();
    const ranToday = b.status === 'at_school' || f.runs.some((r) => r.busId === b.id && r.finished && r.finished.toDateString() === todayStr && r.type !== 'refuel');
    return ranToday ? { tone: 'ok', label: 'Run done' } : { tone: 'mute', label: 'Not on a run' };
  };

  const parentBuses = (p: Person) => {
    const names = new Set<string>();
    p.children.forEach((c) => { const n = busName(c.busId); if (n) names.add(n); });
    if (!names.size && p.busName) names.add(busName(p.busId) || p.busName);
    return Array.from(names).join(', ');
  };

  const needle = q.trim().toLowerCase();
  const digits = needle.replace(/\D/g, '');
  const matches = (p: Person, extra: string) => !needle
    || `${p.name} ${p.email} ${extra}`.toLowerCase().includes(needle)
    || (digits.length >= 3 && p.phone.replace(/\D/g, '').includes(digits));
  const drivers = f.drivers.filter((p) => matches(p, busOf(p)?.name || p.busName));
  const parents = f.parents.filter((p) => matches(p, `${p.children.map((c) => c.name).join(' ')} ${parentBuses(p)}`));
  const linked = Math.max(0, f.childrenTotal - f.childrenUnlinked);

  const people = tab === 'drivers' ? f.drivers : f.parents;
  const rows = tab === 'drivers' ? drivers : parents;

  return (
    <PageBody loading={f.loading} skeleton={<PageSkeleton stats />}>
      <div className={'wrap' + (f.error ? ' stale' : '')} style={{ maxWidth: '1180px' }}>
        {f.error && <StaleBanner what="drivers and parents" since={f.updatedAt} onRetry={f.retry} />}
        <div className="phead">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <h1 className="h1">Drivers and parents</h1>
            <p className="sub">
              {plural(f.drivers.length, 'driver')} · {plural(f.parents.length, 'parent')}{f.childrenTotal ? ` linked to ${plural(linked, 'child', 'children')}` : ''}
            </p>
          </div>
          <div className="row" style={{ gap: '8px' }}>
            <a className="btn b-line" {...go('ConsoleSettings')}>Sign-up code</a>
            {tab === 'drivers' && <button type="button" className="btn b-line" onClick={() => setAddingDriver(true)}>Add a driver</button>}
            <button type="button" className="btn b-ink" onClick={() => setInviting(true)}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
                <path d="M12 5v14M5 12h14" />
              </svg>
              {tab === 'drivers' ? 'Invite drivers' : 'Invite parents'}
            </button>
          </div>
        </div>

        {f.childrenUnlinked > 0 && (
          <div className="panel row" style={{ padding: '14px 18px', gap: '14px', flexWrap: 'wrap' }}>
            <span className="sev" style={{ background: 'var(--warn)', height: '36px' }} />
            <div style={{ flex: '1', minWidth: '240px' }}>
              <b>{plural(f.childrenUnlinked, 'child has', 'children have')} no current parent</b>
              <p className="sm" style={{ margin: '2px 0 0', color: 'var(--muted)' }}>
                Their parent accounts were removed or never linked. Those families get no pickup alerts.
              </p>
            </div>
            <button type="button" className="btn s b-ink" onClick={() => setUnlinkedOpen(true)}>Fix {plural(f.childrenUnlinked, 'row')}</button>
          </div>
        )}

        <FamilyLinkRequests orgCode={f.org.code} />

        <section className="panel" style={{ overflow: 'hidden' }}>
          <div className="row" style={{ padding: '12px 16px', borderBottom: '1px solid var(--line)', flexWrap: 'wrap', gap: '10px' }}>
            <div className="seg" role="tablist" aria-label="Drivers or parents">
              <button type="button" role="tab" aria-selected={tab === 'drivers'} className={tab === 'drivers' ? 'on' : ''} onClick={() => setParam('tab', 'drivers')}>Drivers {n0(f.drivers.length)}</button>
              <button type="button" role="tab" aria-selected={tab === 'parents'} className={tab === 'parents' ? 'on' : ''} onClick={() => setParam('tab', 'parents')}>Parents {n0(f.parents.length)}</button>
            </div>
            <div className="zinput" style={{ height: '40px', width: '280px', maxWidth: '100%', borderRadius: '8px', marginLeft: 'auto' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
              <input
                type="text" value={q} onChange={(e) => setParam('q', e.target.value)}
                placeholder={tab === 'drivers' ? 'Search name, phone or bus' : 'Search parent, child or phone'}
                aria-label={tab === 'drivers' ? 'Search drivers' : 'Search parents'}
              />
            </div>
          </div>

          {people.length === 0 ? (
            tab === 'drivers' ? (
              <Empty title="No drivers yet" body="Drivers join with your sign-up code in the Trasse app, then you assign each one a bus.">
                <a className="btn b-ink" {...go('ConsoleSettings')}>Get a sign-up code</a>
              </Empty>
            ) : (
              <Empty title="No parents yet" body="Parents join with your sign-up code and link their children. Then they get pickup alerts.">
                <a className="btn b-ink" {...go('ConsoleSettings')}>Get a sign-up code</a>
              </Empty>
            )
          ) : rows.length === 0 ? (
            <NoResults
              what={tab === 'drivers' ? 'driver' : 'parent'} q={q}
              hint={tab === 'drivers' ? 'Search by name, phone number or bus. Check the spelling, or try part of the name.' : 'Search by parent name, child name, phone or bus. Check the spelling, or try part of the name.'}
              onClear={() => setParam('q', '')}
            />
          ) : tab === 'drivers' ? (
            <div className="tblbox">
              <table className="t">
                <thead>
                  <tr><th>Driver</th><th>Phone</th><th>Bus</th><th>Today</th><th>Last seen</th></tr>
                </thead>
                <tbody>
                  {drivers.map((p) => {
                    const b = busOf(p);
                    const t = today(p);
                    return (
                      <tr key={p.id} onClick={() => openPerson(p.id)} style={{ cursor: 'pointer' }} className={personId === p.id ? 'sel' : undefined}>
                        <td><div className="row"><span className="av">{initials(p.name)}</span><b>{p.name}</b></div></td>
                        <td className="num">{maskPhone(p.phone) || <Dash />}</td>
                        <td>{b ? <a {...go('ConsoleBuses', `?bus=${encodeURIComponent(b.id)}`)} style={{ color: 'inherit' }}>{b.name}</a> : (p.busName || <span style={{ color: 'var(--faint)' }}>No bus</span>)}</td>
                        <td><span className={`chip c-${t.tone}`}><span className="d" />{t.label}</span></td>
                        <td style={{ color: 'var(--muted)' }}>{p.lastSeen ? ago(p.lastSeen) : <Dash />}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="tblbox">
              <table className="t">
                <thead>
                  <tr><th>Parent</th><th>Phone</th><th>Children</th><th>Bus</th><th>Alerts</th></tr>
                </thead>
                <tbody>
                  {parents.map((p) => {
                    const buses = parentBuses(p);
                    const chip: { tone: ChipTone; label: string } = p.invite === 'none'
                      ? { tone: 'mute', label: 'Account suspended' }
                      : p.children.length === 0 ? { tone: 'warn', label: 'No child linked' } : { tone: 'ok', label: 'Gets alerts' };
                    return (
                      <tr key={p.id} onClick={() => openPerson(p.id)} style={{ cursor: 'pointer' }} className={personId === p.id ? 'sel' : undefined}>
                        <td><b>{p.name}</b></td>
                        <td className="num">{maskPhone(p.phone) || <Dash />}</td>
                        <td style={{ maxWidth: 340 }}><ChildNames names={p.children.map((c) => c.name)} /></td>
                        <td>{buses || <Dash />}</td>
                        <td><span className={`chip c-${chip.tone}`}><span className="d" />{chip.label}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
      {person && <PersonSheet f={f} person={person} top={sheetTop} onClose={closePerson} />}
      {inviting && <InviteModal f={f} initialRole={tab === 'drivers' ? 'bus_driver' : 'parent'} onClose={() => setInviting(false)} />}
      {addingDriver && <AddDriverModal f={f} onClose={() => setAddingDriver(false)} />}
      {unlinkedOpen && <UnlinkedModal f={f} onClose={() => setUnlinkedOpen(false)} />}
    </PageBody>
  );
};

export default People;
